import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	MenuItem,
	TextField,
	Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ArrowBackIosOutlined, ArrowForwardIosOutlined } from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'
import moment from 'moment'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { gray, purple } from '../../../utils/colors'
import {
	formatNumsToDollars,
	NON_BREAKING_SPACE,
	PROMO_SERVICE_TARGETS,
	PROMO_API_ACTIONS,
	calculatePromoAmountToApply,
	DAYCARE
} from '../../../utils'
import PromoOnlyEntryDialog from '../Promos/PromoOnlyEntryDialog'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'

const OTHER = 'other'

const CREDIT_QUANTITY_OPTIONS = {
	[DAYCARE]: [1, 5, 10, 20, 50],
	[OTHER]: [1, 5, 15]
}

const useStyles = makeStyles((theme) => ({
	dialogActions: {
		justifyContent: 'flex-start'
	},
	dialogTitle: {
		marginBottom: 24,
		fontSize: 16,
		textTransform: 'uppercase'
	},
	servicesWrapper: {
		marginBottom: '1.5rem'
	},
	serviceWrapper: {
		display: 'flex',
		alignItems: 'center',
		gap: '1rem',
		'& span': {
			width: '20%',
			fontSize: 12,
			letterSpacing: 0.75,
			textTransform: 'uppercase'
		}
	},
	contentWrapper: {
		display: 'flex',
		flexDirection: 'row',
		gap: '1.5rem',
		marginBottom: '2.25rem'
	},
	formWrapper: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: '1.25rem'
	},
	formColumnsWrapper: {
		flex: 1,
		display: 'flex',
		flexDirection: 'row',
		gap: '1.25rem'
	},
	formColumnWrapper: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: '1.25rem'
	},
	summaryWrapper: {
		backgroundColor: gray[200],
		flexBasis: '16rem',
		padding: '1.125rem',
		borderRadius: '0.375rem',
		'& h2': {
			fontSize: '1rem',
			color: gray[800]
		},
		display: 'flex',
		flexDirection: 'column',
		gap: '.5rem'
	},
	summaryTableWrapper: {
		flex: 1,
		display: 'flex',
		'& table': {
			width: '100%',
			color: gray[800],
			borderWidth: '1px',
			borderColor: gray[300],
			borderStyle: 'solid',
			borderLeftWidth: 0,
			borderRightWidth: 0,
			flex: 1,
			'& tr': {
				'& th': {
					fontWeight: 600,
					textAlign: 'left',
					fontSize: '0.75rem',
					textTransform: 'uppercase',
					padding: 0
				},
				'& td': {
					textAlign: 'left',
					fontSize: '0.875rem',
					padding: 0
				},
				'& th:first-child': {
					width: '55%'
				},
				'& td:first-child': {
					paddingRight: '2%'
				},
				'& th:last-child': {
					textAlign: 'right'
				},
				'& td:last-child': {
					textAlign: 'right'
				}
			}
		}
	},
	summaryTotalWrapper: {
		display: 'flex',
		fontSize: '1rem',
		'& div:first-child': {
			flex: 1
		}
	},
	fieldWrapper: {
		'& .MuiFilledInput-root': {
			minWidth: '100%'
		},
		'& .MuiTextField-root': {
			minWidth: '100%'
		}
	},
	disableButton: {
		color: '#D02C2C',
		fontSize: '14px',
		lineHeight: '19px',
		borderRadius: '25px',
		marginBottom: 32,
		'&:hover': {
			backgroundColor: 'rgba(208, 44, 44, 0.2)'
		}
	},
	close: {
		position: 'absolute',
		top: 10,
		right: 10,
		padding: 10,
		fontFamily: 'Nunito',
		fontWeight: '800',
		fontSize: 12,
		cursor: 'pointer'
	},
	datePicker: {
		margin: 0
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	calendarButton: {
		'& svg': {
			fill: '#777679'
		}
	},
	addPromoButton: {
		color: purple[500],
		fontSize: '0.875rem',
		letterSpacing: 1.25,
		padding: 0
	}
}))

const blankFacility = {
	services: {
		daycare: {
			prices: {}
		}
	},
	market_id: '',
	market: {
		prices: {}
	}
}

const AddCreditDialog = (props) => {
	const classes = useStyles()
	// const selected = useSelector((state) => state.specific.households.selected) // @TODO not used
	const facilities = useSelector((state) => state.specific.facilities.facilities)

	let { selectedCredit, isShown, setIsShown, addCredits, householdId } = props

	const { serviceTarget, marketId } = selectedCredit || { serviceTarget: null, marketId: null }

	const [creditQuantityOptions, setCreditQuantityOptions] = useState([])
	const [isOpen, setIsOpen] = useState(false)
	const [creditQuantity, setCreditQuantity] = useState('')
	const [creditQuantityError, setCreditQuantityError] = useState(false)
	const [credit, setCredit] = useState(selectedCredit)
	const [discount, setDiscount] = useState('')
	const [description, setDescription] = useState('')
	const [expirationDate, setExpirationDate] = useState(moment().add(1, 'year'))
	const [type, setType] = useState('')
	const [promoEntryDialogOpen, setPromoEntryDialogOpen] = useState(false)
	const [appliedPromo, setAppliedPromo] = useState(null)

	const [facility, setFacility] = useState(blankFacility)

	const { totalCreditAvailable } = useSelectedHousehold()

	useEffect(() => {
		if (selectedCredit) {
			let fac = blankFacility
			const { facilityId, marketId } = selectedCredit

			if (facilityId) {
				fac = facilities.find(({ _id }) => _id === facilityId)
			}

			if (marketId) {
				fac = facilities.find(({ market_id, skiptown }) => market_id === marketId && !!skiptown)
			}

			setFacility(fac)
		}
	}, [selectedCredit, facilities])

	useEffect(() => {
		if (selectedCredit) {
			setCredit({ ...selectedCredit })

			const service = selectedCredit?.service?.toLowerCase() || ''
			if (service.includes(DAYCARE)) {
				setCreditQuantityOptions(CREDIT_QUANTITY_OPTIONS[DAYCARE])
			} else {
				setCreditQuantityOptions(CREDIT_QUANTITY_OPTIONS[OTHER])
			}

			const { type } = selectedCredit || ''
			if (type === 'grooming') {
				setExpirationDate(moment().add(1, 'month'))
			} else {
				setExpirationDate(moment().add(1, 'year'))
			}
			setType(type)
		}
	}, [selectedCredit])

	const creditPricePerUnit = useMemo(() => {
		if (creditQuantity === '') {
			return 0
		}

		const daycarePrices = facility?.services?.daycare?.prices
		const homeVisitPrices = facility?.market?.prices

		const creditQuantityInt = parseInt(creditQuantity, 10)

		let price = 0

		if (type === DAYCARE) {
			price = daycarePrices[creditQuantityInt] || 0
			return price.toFixed(2)
		}

		if (type === 'grooming') {
			return 3500
		}

		if (homeVisitPrices[type]) {
			const homeVisitPricesPerType = homeVisitPrices[type]
			const homeVisitPricePerQuantity = homeVisitPricesPerType
				? homeVisitPricesPerType[creditQuantityInt]
				: 0
			price = homeVisitPricePerQuantity
		}

		return price.toFixed(2)
	}, [creditQuantity, facility, type])

	const creditCost = useMemo(() => {
		if (creditQuantity === '') {
			return 0
		}

		const creditQuantityInt = parseInt(creditQuantity, 10)

		const cost = creditQuantityInt * creditPricePerUnit

		return cost.toFixed(2)
	}, [creditQuantity, creditPricePerUnit])

	const discountCalculation = useMemo(() => {
		if (discount === '' || creditCost < 0) {
			return 0
		}

		const discountInt = parseInt(discount, 10)
		const percentage = discountInt / 100
		let discountValue = creditCost * percentage

		return discountValue.toFixed(2)
	}, [discount, creditCost])

	const totalCost = useMemo(() => {
		if (creditCost < 0) {
			return 0
		}

		const difference = creditCost - discountCalculation

		return difference
	}, [creditCost, discountCalculation])

	const promoAmountToApply = useMemo(() => {
		if (!appliedPromo) {
			return 0
		}

		return calculatePromoAmountToApply({
			promo: appliedPromo,
			total: totalCost,
			service_target: serviceTarget
		})
	}, [appliedPromo])

	const totalCostWithPromo = useMemo(() => {
		const cost = totalCost + promoAmountToApply
		return cost
	}, [promoAmountToApply, totalCost])

	const totalCreditUsed = useMemo(() => {
		if (totalCreditAvailable === 0) {
			return 0
		}

		let creditUsed = 0
		if (totalCreditAvailable >= totalCostWithPromo) {
			creditUsed = totalCostWithPromo
		} else {
			creditUsed = totalCreditAvailable
		}

		return creditUsed
	}, [totalCostWithPromo, totalCreditAvailable])

	const totalCostWithPromoAndCredit = useMemo(() => {
		const cost = totalCostWithPromo - totalCreditUsed
		return cost
	}, [totalCostWithPromo, totalCreditUsed])

	const closeDialog = () => {
		setDiscount('')
		setCreditQuantity('')
		setCreditQuantityError(false)
		setIsShown(false)
		setExpirationDate(moment().add(1, 'year'))
		setDescription('')
		setAppliedPromo(null)
	}

	const save = () => {
		if (!creditQuantity) {
			setCreditQuantityError(true)
			return
		}

		addCredits({
			facilityId: selectedCredit?.facilityId ? selectedCredit?.facilityId : facility?._id,
			service: credit.type,
			number: creditQuantity,
			discount: discount ? parseInt(discount) : null,
			expiration_date: expirationDate,
			description: description,
			applied_promo: appliedPromo
				? {
					promo_id: appliedPromo._id,
					service_target: serviceTarget,
					household_id: householdId,
					market_id: marketId
				}
				: null
		})

		closeDialog()
	}

	const updateExpirationDate = (date) => {
		setExpirationDate(moment(date).hour(12))
		setIsOpen(false)
	}

	const handleDiscountChange = useCallback(
		({ target: { value } }) => {
			if (!value || value === '') {
				setDiscount('')
				return
			}

			const validNumberRegEx = /^[1-9]$|^[1-9][0-9]$|^(100)$/
			if (!validNumberRegEx.test(value)) return

			setDiscount(value)
		},
		[setDiscount]
	)

	const handlePromoEntryDialogState = useCallback(() => {
		setPromoEntryDialogOpen((prev) => !prev)
	}, [setPromoEntryDialogOpen])

	const handleRemoveAppliedPromo = useCallback(() => {
		setAppliedPromo(null)
	}, [setAppliedPromo])

	const handleOnPromoValidationComplete = useCallback(
		(promo) => {
			setAppliedPromo(promo)
			handlePromoEntryDialogState()
		},

		[setAppliedPromo]
	)

	return (
		<>
			<Dialog
				open={isShown}
				aria-labelledby="form-dialog-title"
				className={classes.dialog}
				onClose={() => closeDialog()}
				PaperProps={{
					style: {
						minWidth: '920px',
						maxWidth: '920px',
						width: '920px'
					}
				}}>
				<DialogTitle className={classes.dialogTitle}>Add Credits</DialogTitle>
				<DialogContent>
					<div className={classes.servicesWrapper}>
						<div className={classes.serviceWrapper}>
							<Typography variant="overline">Service</Typography>
							<Typography variant="body2">{credit ? credit.service : ''}</Typography>
						</div>
						<div className={classes.serviceWrapper}>
							<Typography variant="overline">Available Credits</Typography>
							<Typography variant="body2">{credit ? credit.availableCredits : ''}</Typography>
						</div>
					</div>
					<div className={classes.contentWrapper}>
						<div className={classes.formWrapper}>
							<div className={classes.formColumnsWrapper}>
								<div className={classes.formColumnWrapper}>
									<div className={classes.fieldWrapper}>
										<TextField
											select
											label="Quantity"
											variant="filled"
											value={creditQuantity}
											onChange={(e) => setCreditQuantity(e.target.value)}>
											{creditQuantityOptions.map((creditQuantityOption) => (
												<MenuItem key={creditQuantityOption} value={creditQuantityOption}>
													{creditQuantityOption === 1
														? `${creditQuantityOption} Credit`
														: `${creditQuantityOption} Credits`}
												</MenuItem>
											))}
										</TextField>
									</div>
									<div className={classes.fieldWrapper}>
										<TextField
											label="Discount"
											variant="filled"
											value={discount}
											disabled={appliedPromo}
											onChange={handleDiscountChange}
											InputProps={{
												endAdornment: <InputAdornment position="end">%</InputAdornment>
											}}
										/>
									</div>
								</div>
								<div className={classes.formColumnWrapper}>
									<div className={classes.fieldWrapper}>
										<KeyboardDatePicker
											disableToolbar
											open={isOpen}
											onOpen={() => setIsOpen(true)}
											onClose={() => setIsOpen(false)}
											variant="inline"
											format="MM/DD/YYYY"
											margin="normal"
											label="Expiration Date"
											inputVariant="filled"
											value={expirationDate}
											onChange={(date) => updateExpirationDate(date)}
											className={classes.datePicker}
											rightArrowIcon={
												<ArrowForwardIosOutlined
													className={classes.calendarHeaderButtons}
												/>
											}
											leftArrowIcon={
												<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />
											}
											KeyboardButtonProps={{
												'aria-label': 'Expiration Date',
												className: classes.calendarButton
											}}
										/>
									</div>
								</div>
							</div>
							<div className={classes.fieldWrapper}>
								<TextField
									label="Description"
									variant="filled"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									inputProps={{ maxLength: 50 }}
									style={{ width: '100%' }}
								/>
							</div>
							{serviceTarget && serviceTarget === PROMO_SERVICE_TARGETS.DAYCARE_CREDITS_ONLY ? (
								<div className={classes.fieldWrapper}>
									<Button
										onClick={() =>
											appliedPromo
												? handleRemoveAppliedPromo()
												: handlePromoEntryDialogState()
										}
										className={classes.addPromoButton}
										disabled={discount > '' || creditQuantity === ''}
										variant="text">
										{appliedPromo ? 'Remove Promo' : 'Add Promo'}
									</Button>
								</div>
							) : null}
						</div>
						<div className={classes.summaryWrapper}>
							<Typography variant="h2">Summary</Typography>
							<div className={classes.summaryTableWrapper}>
								<table>
									<thead>
										<tr>
											<th>Item</th>
											<th>Qty</th>
											<th>Cost</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td>
												{credit && creditQuantity > ''
													? `${credit.service} Credit`
													: NON_BREAKING_SPACE}
											</td>
											<td>
												{creditQuantity > '' ? `${creditQuantity}x` : NON_BREAKING_SPACE}
											</td>
											<td>
												{creditPricePerUnit > 0
													? formatNumsToDollars(creditPricePerUnit)
													: NON_BREAKING_SPACE}
											</td>
										</tr>
										<tr>
											<td>{discount > '' ? `${discount}% Discount` : NON_BREAKING_SPACE}</td>
											<td>{discount > '' ? '1x' : NON_BREAKING_SPACE}</td>
											<td>
												{discountCalculation > 0
													? `-${formatNumsToDollars(discountCalculation)}`
													: NON_BREAKING_SPACE}
											</td>
										</tr>
										{appliedPromo ? (
											<tr>
												<td>{appliedPromo.code}</td>
												<td>{NON_BREAKING_SPACE}</td>
												<td>-{formatNumsToDollars(-promoAmountToApply)}</td>
											</tr>
										) : null}
										{totalCreditUsed > 0 ? (
											<tr>
												<td>Credit</td>
												<td>{NON_BREAKING_SPACE}</td>
												<td>-{formatNumsToDollars(-totalCreditUsed)}</td>
											</tr>
										) : null}
									</tbody>
								</table>
							</div>
							<div className={classes.summaryTotalWrapper}>
								<div>Total</div>
								<div>{formatNumsToDollars(totalCostWithPromoAndCredit)}</div>
							</div>
						</div>
					</div>
				</DialogContent>
				<DialogActions className={classes.dialogActions}>
					<Button variant="contained" color="secondary" onClick={() => save()}>
						CONFIRM
					</Button>
					<Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
						CANCEL
					</Button>
				</DialogActions>
			</Dialog>
			<PromoOnlyEntryDialog
				open={promoEntryDialogOpen}
				apiAction={PROMO_API_ACTIONS.Validate}
				service_target={serviceTarget}
				household_id={householdId}
				market_id={marketId}
				facility_id={facility?._id || selectedCredit?.facilityId}
				onCancel={handlePromoEntryDialogState}
				onComplete={handleOnPromoValidationComplete}
			/>
		</>
	)
}

export default AddCreditDialog
